import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiSave, FiPlus, FiTrash2, FiSearch, FiCheck } from 'react-icons/fi';
import api from '../../services/api';

const PAYMENT_TYPES = ['Check', 'Wire', 'ACH', 'Credit Card', 'Paycargo', 'Zelle'];

const PaymentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentId } = useParams();
  const [searchParams] = useSearchParams();
  const modeParam = searchParams.get('mode') || 'receive'; // 'receive' or 'make'
  const isNew = !paymentId || paymentId === 'new';
  const preSelectedInvoices = location.state?.invoices || null;
  const preSelectedParty = location.state?.party || '';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [payment, setPayment] = useState(null);
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    payment_type: modeParam,
    party: preSelectedParty,
    post_date: new Date().toISOString().split('T')[0],
    type: 'Check',
    check_no: '',
    bank_name: '',
    bank_currency: 'USD',
    deposit_date: '',
    void_date: '',
    remark: '',
    status: 'Draft',
  });

  // Trade partner dropdown
  const [tradePartners, setTradePartners] = useState([]);
  const [partySearch, setPartySearch] = useState('');
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);

  // Invoice search
  const [showInvoiceSearch, setShowInvoiceSearch] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceResults, setInvoiceResults] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState({}); // { charge_id: paymentAmount }

  const paymentType = payment?.payment_type || formData.payment_type;
  const isReceive = paymentType === 'receive';

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const res = await api.get(`/trade-partners?status=Active`);
        setTradePartners(res.data || []);
      } catch { /* ignore */ }
    };
    loadPartners();
  }, []);

  const filteredPartners = tradePartners.filter(tp =>
    (tp.name || '').toLowerCase().includes((partySearch || formData.party || '').toLowerCase())
  ).slice(0, 20);
  const title = isReceive ? 'Receive Payment' : 'Make Payment';
  const partyLabel = isReceive ? 'Received From' : 'Pay To';
  const chargeType = isReceive ? 'AR' : 'AP';

  const loadPayment = useCallback(async (id) => {
    try {
      const res = await api.get(`/payments/${id}`);
      const data = res.data;
      setPayment(data);
      setItems(data.items || []);
      setFormData({
        payment_type: data.payment_type,
        party: data.party || '',
        post_date: data.post_date ? data.post_date.split('T')[0] : '',
        type: data.type || 'Check',
        check_no: data.check_no || '',
        bank_name: data.bank_name || '',
        bank_currency: data.bank_currency || 'USD',
        deposit_date: data.deposit_date ? data.deposit_date.split('T')[0] : '',
        void_date: data.void_date ? data.void_date.split('T')[0] : '',
        remark: data.remark || '',
        status: data.status || 'Draft',
      });
    } catch (error) {
      toast.error('Failed to load payment');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isNew && paymentId) {
      loadPayment(paymentId);
    } else {
      setLoading(false);
    }
  }, [isNew, paymentId, loadPayment]);

  // Auto-create payment and add pre-selected invoices from Invoice/Cost List
  useEffect(() => {
    if (isNew && preSelectedInvoices && preSelectedInvoices.length > 0 && preSelectedParty) {
      const autoCreate = async () => {
        setSaving(true);
        try {
          const res = await api.post('/payments', {
            payment_type: modeParam,
            party: preSelectedParty,
            post_date: new Date().toISOString().split('T')[0],
            type: 'Check',
            bank_currency: 'USD',
            status: 'Draft',
          });
          const newId = res.data.id;
          const addRes = await api.post(`/payments/${newId}/items`, { items: preSelectedInvoices });
          const resData = addRes.data;
          setItems(resData.items || resData);
          toast.success(`Payment created with ${preSelectedInvoices.length} invoice(s)`);
          navigate(`/payment/${newId}`, { replace: true });
        } catch (error) {
          toast.error('Failed to auto-create payment');
        } finally {
          setSaving(false);
        }
      };
      autoCreate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let currentPaymentId = paymentId;
      const hasDeposit = !!formData.deposit_date;

      // Step 1: Create or update payment header
      if (isNew) {
        const res = await api.post('/payments', formData);
        currentPaymentId = res.data.id;
      } else {
        // For existing payments with items already added but no new invoices selected,
        // auto-apply directly via the PUT endpoint when deposit is set
        const hasNewInvoices = Object.keys(selectedInvoices).length > 0 || (preSelectedInvoices && preSelectedInvoices.length > 0);
        const shouldAutoApplyOnUpdate = hasDeposit && items.length > 0 && !hasNewInvoices && formData.status === 'Draft';
        await api.put(`/payments/${currentPaymentId}`, {
          ...formData,
          auto_apply: shouldAutoApplyOnUpdate,
        });
        if (shouldAutoApplyOnUpdate) {
          toast.success('Payment saved and applied!');
          loadPayment(currentPaymentId);
          setSaving(false);
          return;
        }
      }

      // Step 2: Add selected invoices (from search panel or pre-selected)
      const selected = invoiceResults.filter(inv => selectedInvoices[inv.charge_id] !== undefined);
      const invoicesToAdd = selected.length > 0 ? selected.map(inv => ({
        charge_id: inv.charge_id,
        invoice_no: inv.invoice_no || '',
        post_date: inv.post_date || null,
        invoice_date: inv.invoice_date || null,
        due_date: inv.due_date || null,
        customer: inv.customer || '',
        mbl_no: inv.mbl_no || '',
        hbl_no: inv.hbl_no || '',
        file_no: inv.file_no || '',
        invoice_amount: inv.invoice_amount || 0,
        balance_amount: inv.balance || 0,
        payment_amount: selectedInvoices[inv.charge_id] || inv.balance || 0,
        charge_type: inv.charge_type || chargeType,
        description: inv.description || '',
      })) : (isNew && preSelectedInvoices && preSelectedInvoices.length > 0) ? preSelectedInvoices : null;

      if (invoicesToAdd && invoicesToAdd.length > 0) {
        const addRes = await api.post(`/payments/${currentPaymentId}/items`, {
          items: invoicesToAdd,
          auto_apply: hasDeposit,  // GoFreight-style: save + apply in one step
        });
        const resData = addRes.data;
        setItems(resData.items || resData);

        if (resData.applied) {
          toast.success(`Payment saved and applied — ${invoicesToAdd.length} invoice(s) cleared!`);
        } else {
          toast.success(`${title} saved with ${invoicesToAdd.length} invoice(s)`);
        }

        // Clear search state
        setInvoiceResults(prev => prev.filter(i => !selectedInvoices[i.charge_id]));
        setSelectedInvoices({});
      } else {
        toast.success(isNew ? `${title} created` : `${title} updated`);
      }

      if (isNew) {
        navigate(`/payment/${currentPaymentId}`, { replace: true });
      } else {
        loadPayment(currentPaymentId);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Build query params for open-invoices API (includes tp_id when available)
  const buildPartyParams = (overrideParty, overrideTpId) => {
    const party = overrideParty || formData.party || '';
    const tpId = overrideTpId || formData.trade_partner_id || '';
    let params = '';
    if (tpId) params += `&tp_id=${tpId}`;
    if (party) params += `&party=${encodeURIComponent(party)}`;
    return params;
  };

  // Search open invoices
  const searchInvoices = async (q) => {
    setInvoiceSearch(q);
    if (q.length < 1) { setInvoiceResults([]); return; }
    setInvoiceLoading(true);
    try {
      const res = await api.get(`/payments/open-invoices?type=${chargeType}&q=${encodeURIComponent(q)}${buildPartyParams()}`);
      // Filter out already-added invoices
      const existingChargeIds = items.map(i => i.charge_id);
      setInvoiceResults(res.data.filter(inv => !existingChargeIds.includes(inv.charge_id)));
    } catch { setInvoiceResults([]); }
    setInvoiceLoading(false);
  };

  const showAllInvoices = async () => {
    setInvoiceLoading(true);
    try {
      const res = await api.get(`/payments/open-invoices?type=${chargeType}${buildPartyParams()}`);
      const existingChargeIds = items.map(i => i.charge_id);
      setInvoiceResults(res.data.filter(inv => !existingChargeIds.includes(inv.charge_id)));
    } catch { setInvoiceResults([]); }
    setInvoiceLoading(false);
    setShowInvoiceSearch(true);
  };

  // Auto-load invoices when a party is selected from the dropdown
  const loadInvoicesForParty = async (partyName, tpId) => {
    setInvoiceLoading(true);
    try {
      const res = await api.get(`/payments/open-invoices?type=${chargeType}${buildPartyParams(partyName, tpId)}`);
      const existingChargeIds = items.map(i => i.charge_id);
      const filtered = res.data.filter(inv => !existingChargeIds.includes(inv.charge_id));
      setInvoiceResults(filtered);
      if (filtered.length > 0) {
        setShowInvoiceSearch(true);
      }
    } catch { setInvoiceResults([]); }
    setInvoiceLoading(false);
  };

  const toggleInvoiceSelect = (inv) => {
    setSelectedInvoices(prev => {
      const next = { ...prev };
      if (next[inv.charge_id] !== undefined) {
        delete next[inv.charge_id];
      } else {
        next[inv.charge_id] = Number(inv.balance) || 0;
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (Object.keys(selectedInvoices).length === invoiceResults.length) {
      setSelectedInvoices({});
    } else {
      const all = {};
      invoiceResults.forEach(inv => { all[inv.charge_id] = Number(inv.balance) || 0; });
      setSelectedInvoices(all);
    }
  };

  const updateSelectedAmount = (chargeId, amount) => {
    setSelectedInvoices(prev => ({ ...prev, [chargeId]: Number(amount) || 0 }));
  };

  const addSelectedInvoices = async () => {
    if (isNew) {
      toast.info('Please save the payment first before adding invoices.');
      return;
    }
    const selected = invoiceResults.filter(inv => selectedInvoices[inv.charge_id] !== undefined);
    if (selected.length === 0) { toast.info('Please select invoices first.'); return; }
    try {
      const newItems = selected.map(inv => ({
        charge_id: inv.charge_id,
        invoice_no: inv.invoice_no || '',
        post_date: inv.post_date || null,
        invoice_date: inv.invoice_date || null,
        due_date: inv.due_date || null,
        customer: inv.customer || '',
        mbl_no: inv.mbl_no || '',
        hbl_no: inv.hbl_no || '',
        file_no: inv.file_no || '',
        invoice_amount: inv.invoice_amount || 0,
        balance_amount: inv.balance || 0,
        payment_amount: selectedInvoices[inv.charge_id] || inv.balance || 0,
        charge_type: inv.charge_type || chargeType,
        description: inv.description || '',
      }));
      const res = await api.post(`/payments/${paymentId}/items`, { items: newItems });
      const resData = res.data;
      setItems(resData.items || resData);
      setInvoiceResults(prev => prev.filter(i => !selectedInvoices[i.charge_id]));
      setSelectedInvoices({});
      loadPayment(paymentId);
      toast.success(`${selected.length} invoice(s) added`);
    } catch (error) {
      toast.error('Failed to add invoices');
    }
  };

  const addInvoiceToPayment = async (invoice) => {
    if (isNew) {
      toast.info('Please save the payment first before adding invoices.');
      return;
    }
    try {
      const newItem = {
        charge_id: invoice.charge_id,
        invoice_no: invoice.invoice_no || '',
        post_date: invoice.post_date || null,
        invoice_date: invoice.invoice_date || null,
        due_date: invoice.due_date || null,
        customer: invoice.customer || '',
        mbl_no: invoice.mbl_no || '',
        hbl_no: invoice.hbl_no || '',
        file_no: invoice.file_no || '',
        invoice_amount: invoice.invoice_amount || 0,
        balance_amount: invoice.balance || 0,
        payment_amount: invoice.balance || 0, // Default to full balance
        charge_type: invoice.charge_type || chargeType,
        description: invoice.description || '',
      };
      const res = await api.post(`/payments/${paymentId}/items`, { items: [newItem] });
      const resData = res.data;
      setItems(resData.items || resData);
      // Remove from search results
      setInvoiceResults(prev => prev.filter(i => i.charge_id !== invoice.charge_id));
      // Reload payment to get updated total
      loadPayment(paymentId);
      toast.success('Invoice added');
    } catch (error) {
      toast.error('Failed to add invoice');
    }
  };

  const updateItemAmount = async (itemId, amount) => {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, payment_amount: amount } : it));
    try {
      const res = await api.put(`/payments/${paymentId}/items/${itemId}`, { payment_amount: Number(amount) || 0 });
      setPayment(prev => prev ? { ...prev, total_amount: res.data.total_amount } : prev);
    } catch {
      toast.error('Failed to update');
    }
  };

  const removeItem = async (itemId) => {
    try {
      const res = await api.delete(`/payments/${paymentId}/items/${itemId}`);
      setItems(prev => prev.filter(it => it.id !== itemId));
      setPayment(prev => prev ? { ...prev, total_amount: res.data.total_amount } : prev);
      toast.success('Invoice removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this payment? Any applied amounts will be reversed.')) return;
    try {
      await api.delete(`/payments/${paymentId}`);
      toast.success('Payment deleted');
      navigate(isReceive ? '/payment-received-list' : '/payment-made-list');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const formatCurrency = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatDate = (v) => v ? new Date(v).toLocaleDateString() : '-';
  const totalPayment = items.reduce((s, i) => s + (Number(i.payment_amount) || 0), 0);

  if (loading) return <div className="loading"><div className="loading-spinner"></div></div>;

  return (
    <div className="shipment-page">
      {/* Breadcrumb */}
      <div className="shipment-breadcrumb">
        <button className="breadcrumb-back-btn" onClick={() => navigate(isReceive ? '/payment-received-list' : '/payment-made-list')} title="Back">
          <FiArrowLeft />
        </button>
        <span>Home</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span>Accounting</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span>Payment</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span className="breadcrumb-current">{title}</span>
      </div>

      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>{title}</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={handleSave} disabled={saving || formData.status === 'Applied'} style={{
            padding: '8px 20px',
            backgroundColor: formData.deposit_date && formData.status !== 'Applied' ? '#059669' : '#3b82f6',
            color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '6px',
            opacity: formData.status === 'Applied' ? 0.6 : 1,
          }}>
            {formData.deposit_date && formData.status !== 'Applied' ? <FiCheck /> : <FiSave />}
            {saving ? 'Saving...' : formData.deposit_date && formData.status !== 'Applied' ? 'Save & Apply' : 'Save'}
          </button>
          {!isNew && (
            <button onClick={handleDelete} style={{ padding: '8px 16px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FiTrash2 /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Status badge */}
      {payment && (
        <div style={{ marginBottom: '12px' }}>
          <span style={{
            padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
            backgroundColor: payment.status === 'Applied' ? '#d1fae5' : payment.status === 'Void' ? '#fde8e8' : '#e0e7ff',
            color: payment.status === 'Applied' ? '#059669' : payment.status === 'Void' ? '#dc2626' : '#4338ca',
          }}>
            {payment.status}
          </span>
          <span style={{ marginLeft: '12px', fontSize: '14px', color: '#64748b' }}>{payment.payment_no}</span>
        </div>
      )}

      {/* Payment Info Section */}
      <div className="section-card" style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '20px' }}>
        <div style={{ borderLeft: '3px solid #3b82f6', paddingLeft: '12px', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>PAYMENT INFORMATION</h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
              <label style={{ width: '100px', fontSize: '12px', fontWeight: 600, color: '#dc2626' }}>*{partyLabel}</label>
              <div style={{ flex: 1, position: 'relative', display: 'flex', gap: '4px' }}>
                <input
                  type="text"
                  className="form-input"
                  value={showPartyDropdown ? partySearch : formData.party}
                  onChange={(e) => {
                    setPartySearch(e.target.value);
                    setShowPartyDropdown(true);
                    setFormData(prev => ({ ...prev, party: e.target.value }));
                  }}
                  onFocus={() => { setPartySearch(formData.party || ''); setShowPartyDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowPartyDropdown(false), 200)}
                  style={{ flex: 1, fontSize: '13px', padding: '6px 10px' }}
                  placeholder="Type to search..."
                />
                <button
                  type="button"
                  onClick={() => window.open('/trade-partner/new', '_blank')}
                  title="Add new trade partner"
                  style={{
                    padding: '4px 10px', backgroundColor: '#3b82f6', color: '#fff',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '16px',
                    fontWeight: 700, lineHeight: '1', flexShrink: 0,
                  }}
                >+</button>
                {showPartyDropdown && filteredPartners.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px',
                    maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    {filteredPartners.map(tp => (
                      <div
                        key={tp.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setFormData(prev => ({ ...prev, party: tp.name, trade_partner_id: tp.id }));
                          setPartySearch(tp.name);
                          setShowPartyDropdown(false);
                          // Auto-load open invoices for the selected party
                          setTimeout(() => {
                            loadInvoicesForParty(tp.name, tp.id);
                          }, 100);
                        }}
                        style={{
                          padding: '6px 10px', fontSize: '13px', cursor: 'pointer',
                          borderBottom: '1px solid #f1f5f9',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        <div style={{ fontWeight: 500 }}>{tp.name}</div>
                        {tp.city && <div style={{ fontSize: '11px', color: '#94a3b8' }}>{tp.city}{tp.state ? `, ${tp.state}` : ''}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ width: '100px', fontSize: '12px', fontWeight: 600, color: '#dc2626' }}>*Post Date</label>
              <input type="date" className="form-input" name="post_date" value={formData.post_date} onChange={handleChange} style={{ flex: 1, fontSize: '13px', padding: '6px 10px' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ width: '100px', fontSize: '12px', fontWeight: 600 }}>Type</label>
              <select className="form-select" name="type" value={formData.type} onChange={handleChange} style={{ flex: 1, fontSize: '13px', padding: '6px 10px' }}>
                {PAYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ width: '100px', fontSize: '12px', fontWeight: 600 }}>Check No.</label>
              <input type="text" className="form-input" name="check_no" value={formData.check_no} onChange={handleChange} style={{ flex: 1, fontSize: '13px', padding: '6px 10px' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ width: '100px', fontSize: '12px', fontWeight: 600 }}>{isReceive ? 'Deposit' : 'Clear'}</label>
              <input type="date" className="form-input" name="deposit_date" value={formData.deposit_date} onChange={handleChange} style={{ flex: 1, fontSize: '13px', padding: '6px 10px' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ width: '100px', fontSize: '12px', fontWeight: 600 }}>Void</label>
              <input type="date" className="form-input" name="void_date" value={formData.void_date} onChange={handleChange} style={{ flex: 1, fontSize: '13px', padding: '6px 10px' }} />
            </div>
          </div>

          {/* Middle column - Bank info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ width: '100px', fontSize: '12px', fontWeight: 600 }}>Bank</label>
              <input type="text" className="form-input" name="bank_name" value={formData.bank_name} onChange={handleChange} style={{ flex: 1, fontSize: '13px', padding: '6px 10px' }} placeholder="e.g., Chase Bank-Checking" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ width: '100px', fontSize: '12px', fontWeight: 600 }}>Currency</label>
              <input type="text" className="form-input" name="bank_currency" value={formData.bank_currency} onChange={handleChange} style={{ flex: 1, fontSize: '13px', padding: '6px 10px' }} />
            </div>
          </div>

          {/* Right column - Remark */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Remark</label>
            <textarea name="remark" value={formData.remark} onChange={handleChange} rows={5} style={{ width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', resize: 'vertical' }} />
          </div>
        </div>
      </div>

      {/* Invoice List Section */}
      <div className="section-card" style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ borderLeft: '3px solid #3b82f6', paddingLeft: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>
              {isReceive ? 'INVOICE LIST' : 'COST LIST'}
            </h3>
          </div>
          {!isNew && formData.status === 'Draft' && (
            <button onClick={showAllInvoices} style={{ padding: '6px 14px', backgroundColor: '#059669', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <FiPlus size={14} /> Add Invoice(s)
            </button>
          )}
        </div>

        {/* Invoice search modal */}
        {showInvoiceSearch && (
          <div style={{ marginBottom: '16px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <FiSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  value={invoiceSearch}
                  onChange={(e) => searchInvoices(e.target.value)}
                  placeholder="Search by invoice no, party, file no..."
                  style={{ width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                  autoFocus
                />
              </div>
              {Object.keys(selectedInvoices).length > 0 && (
                <button onClick={addSelectedInvoices}
                  style={{ padding: '8px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <FiPlus size={12} style={{ marginRight: '4px', verticalAlign: '-1px' }} />
                  Add Selected ({Object.keys(selectedInvoices).length})
                </button>
              )}
              <button onClick={() => { setShowInvoiceSearch(false); setInvoiceResults([]); setInvoiceSearch(''); setSelectedInvoices({}); }}
                style={{ padding: '8px 12px', background: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                Close
              </button>
            </div>
            {invoiceLoading && <div style={{ fontSize: '12px', color: '#94a3b8', padding: '8px' }}>Loading...</div>}
            {invoiceResults.length > 0 && (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#e2e8f0', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={{ padding: '6px 8px', width: '30px' }}>
                        <input type="checkbox" checked={Object.keys(selectedInvoices).length === invoiceResults.length && invoiceResults.length > 0} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
                      </th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Invoice No.</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>{isReceive ? 'Customer' : 'Vendor'}</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>File No.</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>MB/L No.</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Invoice Amt</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Balance</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceResults.map(inv => {
                      const isSelected = selectedInvoices[inv.charge_id] !== undefined;
                      return (
                        <tr key={inv.charge_id} style={{ borderBottom: '1px solid #f1f5f9', background: isSelected ? '#eff6ff' : '#fff' }}>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <input type="checkbox" checked={isSelected} onChange={() => toggleInvoiceSelect(inv)} style={{ cursor: 'pointer' }} />
                          </td>
                          <td style={{ padding: '6px 8px', color: '#2563eb', cursor: 'pointer' }} onClick={() => toggleInvoiceSelect(inv)}>{inv.invoice_no || '-'}</td>
                          <td style={{ padding: '6px 8px' }}>{inv.customer || '-'}</td>
                          <td style={{ padding: '6px 8px' }}>{inv.file_no || '-'}</td>
                          <td style={{ padding: '6px 8px' }}>{inv.mbl_no || '-'}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(inv.invoice_amount)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(inv.balance)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                            {isSelected ? (
                              <input
                                type="number" step="0.01"
                                value={selectedInvoices[inv.charge_id]}
                                onChange={(e) => updateSelectedAmount(inv.charge_id, e.target.value)}
                                style={{ width: '100px', textAlign: 'right', padding: '3px 6px', border: '1px solid #3b82f6', borderRadius: '4px', fontSize: '12px', background: '#eff6ff' }}
                              />
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* Totals row */}
            {invoiceResults.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 8px', borderTop: '2px solid #e2e8f0', marginTop: '4px', gap: '30px' }}>
                <div>
                  <span style={{ fontSize: '12px', fontWeight: 600 }}>Invoice Amt: </span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#2563eb' }}>{formatCurrency(invoiceResults.reduce((s, i) => s + (Number(i.invoice_amount) || 0), 0))}</span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', fontWeight: 600 }}>Balance: </span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#dc2626' }}>{formatCurrency(invoiceResults.reduce((s, i) => s + (Number(i.balance) || 0), 0))}</span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', fontWeight: 600 }}>Selected Payment: </span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#059669' }}>{formatCurrency(Object.values(selectedInvoices).reduce((s, v) => s + v, 0))}</span>
                </div>
              </div>
            )}
            {!invoiceLoading && invoiceResults.length === 0 && invoiceSearch.length > 0 && (
              <div style={{ fontSize: '12px', color: '#94a3b8', padding: '8px', textAlign: 'center' }}>No open invoices found</div>
            )}
          </div>
        )}

        {/* Payment Items Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Post Date</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Invoice Date</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Due Date</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>{isReceive ? 'Customer' : 'Vendor'}</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Invoice No.</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>MB/L No.</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>HB/L No.</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>File No.</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Invoice AMT</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Balance AMT</th>
                <th style={{ padding: '8px', textAlign: 'right', color: '#059669', fontWeight: 700 }}>Payment</th>
                {formData.status === 'Draft' && <th style={{ padding: '8px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={formData.status === 'Draft' ? 12 : 11} style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
                    No invoices added yet. Click "Add Invoice(s)" to select open invoices.
                  </td>
                </tr>
              ) : (
                items.map(item => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px' }}>{formatDate(item.post_date)}</td>
                    <td style={{ padding: '6px 8px' }}>{formatDate(item.invoice_date)}</td>
                    <td style={{ padding: '6px 8px' }}>{formatDate(item.due_date)}</td>
                    <td style={{ padding: '6px 8px' }}>{item.customer || '-'}</td>
                    <td style={{ padding: '6px 8px', color: '#2563eb' }}>{item.invoice_no || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{item.mbl_no || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{item.hbl_no || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{item.file_no || '-'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(item.invoice_amount)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(item.balance_amount)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                      {formData.status === 'Draft' ? (
                        <input
                          type="number"
                          step="0.01"
                          value={item.payment_amount}
                          onChange={(e) => updateItemAmount(item.id, e.target.value)}
                          style={{ width: '100px', textAlign: 'right', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                        />
                      ) : (
                        <span style={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(item.payment_amount)}</span>
                      )}
                    </td>
                    {formData.status === 'Draft' && (
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <button onClick={() => removeItem(item.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                          onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Totals row */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 8px', borderTop: '2px solid #e2e8f0', marginTop: '8px', gap: '40px' }}>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '12px', color: '#64748b' }}>{items.length} item(s) selected</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '12px', fontWeight: 600 }}>Invoice Amount: </span>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#2563eb' }}>{formatCurrency(items.reduce((s, i) => s + (Number(i.invoice_amount) || 0), 0))}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '12px', fontWeight: 600 }}>Balance Amount: </span>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#dc2626' }}>{formatCurrency(items.reduce((s, i) => s + (Number(i.balance_amount) || 0), 0))}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '12px', fontWeight: 600 }}>Paid Amount: </span>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#059669' }}>{formatCurrency(totalPayment)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;
